import { Box, Typography } from "@mui/material";
import { Map, Marker } from "@vis.gl/react-google-maps";
import React from "react";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MapIcon from "@mui/icons-material/Map";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
} from "@mui/material";
import { AddressPropTypes } from "../../schema/address";
import twoLineAddressFromProps, { labelForLocation } from "../../helper/address";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { SIDEBAR } from "../CompanyModal/v2/sidebarVisual";
import useGeocodedLocation, { geocodableAddress } from "../../hooks/useGeocodedLocation";

const CaptionLikeDetails = styled(Typography)(() => ({
  display: "block",
  gutterBottom: false,
}));

const MAP_STYLES = [
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
];

function buildPrimaryTitle(location, line1, line2) {
  const labeled = labelForLocation(location);
  if (labeled && labeled !== "N/A") return labeled;
  if (location?.city && location?.region1) {
    return `${location.city}, ${location.region1}`;
  }
  return line1 || line2 || location?.fullAddress || "Location";
}

function buildFullAddress(location, line1, line2) {
  if (location?.fullAddress) return location.fullAddress;
  return [line1, line2].filter(Boolean).join(", ");
}

function LocationMap({ location, height }) {
  const { status, point } = useGeocodedLocation(location, geocodableAddress(location));
  if (status !== "ready") {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          borderRadius: "10px",
          border: "1px solid rgba(88, 89, 91, 0.14)",
          bgcolor: "rgba(88, 89, 91, 0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          {status === "loading" ? "Loading map…" : "Map unavailable"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height,
        borderRadius: "10px",
        overflow: "hidden",
        border: "1px solid rgba(88, 89, 91, 0.12)",
        filter: "hue-rotate(30deg)",
        // Keep map pan/zoom from fighting parent bottom-sheet scroll.
        touchAction: "pan-x pan-y",
        overscrollBehavior: "contain",
      }}
    >
      <Map
        defaultZoom={11}
        defaultCenter={point}
        disableDefaultUI={false}
        gestureHandling="cooperative"
        styles={MAP_STYLES}
        style={{ width: "100%", height: "100%" }}
      >
        <Marker position={point} />
      </Map>
    </Box>
  );
}

LocationMap.propTypes = {
  location: AddressPropTypes.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

/**
 * Full-width Location panel: title hierarchy + address + map spanning content width.
 */
function LocationPanel({ location, mb, showSectionTitle = false, mapHeight = 240 }) {
  const [line1, line2] = twoLineAddressFromProps({ location });
  const primaryTitle = buildPrimaryTitle(location, line1, line2);
  const fullAddress = buildFullAddress(location, line1, line2);

  const formattedLat =
    location?.lat != null
      ? Math.round((location.lat + Number.EPSILON) * 100000) / 100000
      : "";
  const formattedLon =
    location?.long != null
      ? Math.round((location.long + Number.EPSILON) * 100000) / 100000
      : "";

  const metaParts = [];
  if (location?.msa) metaParts.push(`MSA: ${String(location.msa).replace(/^MSA:\s*/i, "")}`);
  if (location?.county) {
    metaParts.push(
      `County: ${String(location.county).replace(/^COUNTY:\s*/i, "")}`
    );
  }
  if (formattedLat !== "" && formattedLon !== "") {
    metaParts.push(`${formattedLat}, ${formattedLon}`);
  }

  return (
    <Box
      data-testid="location-panel"
      sx={{ width: "100%", mb: mb || 0 }}
    >
      {showSectionTitle ? (
        <Typography
          sx={{
            ...SIDEBAR.type.sectionTitle,
            mb: SIDEBAR.gap.sectionTitleToContent,
          }}
        >
          Location
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: SIDEBAR.gap.withinCard,
          mb: SIDEBAR.gap.labelToValue,
        }}
      >
        <PlaceOutlinedIcon
          sx={{
            fontSize: 18,
            color: "primary.main",
            mt: "2px",
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.35,
              letterSpacing: "-0.01em",
              color: "text.primary",
            }}
          >
            {primaryTitle}
          </Typography>
          {fullAddress && fullAddress !== primaryTitle ? (
            <Typography
              sx={{
                mt: SIDEBAR.gap.labelToValue,
                ...SIDEBAR.type.bodySecondary,
              }}
            >
              {fullAddress}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {metaParts.length > 0 ? (
        <Typography
          sx={{
            ml: "26px",
            mb: SIDEBAR.gap.listItem,
            ...SIDEBAR.type.meta,
            color: "text.disabled",
          }}
        >
          {metaParts.join(" · ")}
        </Typography>
      ) : (
        <Box sx={{ mb: SIDEBAR.gap.listItem }} />
      )}

      <LocationMap location={location} height={mapHeight} />
    </Box>
  );
}

LocationPanel.propTypes = {
  location: AddressPropTypes.isRequired,
  mb: PropTypes.any,
  showSectionTitle: PropTypes.bool,
  mapHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

/** Legacy bordered accordion card (Company Detail page / collapsed addresses). */
function LocationAccordionCard({ location, mb, expanded }) {
  const [line1, line2] = twoLineAddressFromProps({ location });
  const formattedLat = location?.lat
    ? Math.round((location.lat + Number.EPSILON) * 100000) / 100000
    : "";
  const formattedLon = location?.long
    ? Math.round((location.long + Number.EPSILON) * 100000) / 100000
    : "";

  return (
    <Box
      sx={{
        border: "1px solid rgba(88, 89, 91, 0.2)",
        borderRadius: "8px",
        mb: mb,
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Accordion
        defaultExpanded={expanded}
        sx={{ width: "100%", maxWidth: "100%", bgcolor: "transparent" }}
        elevation={0}
        // Mount the map only when opened: an address without coordinates is
        // geocoded (billed) on mount, and collapsed cards render in lists.
        TransitionProps={{ unmountOnExit: true }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{ borderRadius: "8px" }}
        >
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "1fr 26px",
            }}
          >
            <Box>
              <Typography variant="body1">{line1}</Typography>
              <Typography variant="body1">{line2}</Typography>
              <Box sx={{ pt: "10px" }}>
                {"msa" in (location || {}) && (
                  <CaptionLikeDetails variant="caption">
                    <b>MSA:</b> {location?.msa}
                  </CaptionLikeDetails>
                )}
                {"county" in (location || {}) && (
                  <CaptionLikeDetails variant="caption">
                    <b>County:</b> {location?.county}
                  </CaptionLikeDetails>
                )}
                {"lat" in (location || {}) && "long" in (location || {}) && (
                  <CaptionLikeDetails variant="caption">
                    <b>Geo:</b> {formattedLat}, {formattedLon}
                  </CaptionLikeDetails>
                )}
              </Box>
            </Box>
            <Box sx={{ justifyContent: "flex-end", alignContent: "center" }}>
              <MapIcon
                sx={{
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.6)"
                      : "#00000099",
                }}
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ margin: "0px", padding: "0px" }}>
          <Divider sx={{ marginTop: "0px", marginBottom: "0px" }} />
          <LocationMap location={location} height={175} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

LocationAccordionCard.propTypes = {
  location: AddressPropTypes.isRequired,
  mb: PropTypes.any,
  expanded: PropTypes.bool,
};

/**
 * @param {"panel"|"card"} variant panel = full-width map-focused layout; card = legacy accordion
 */
export const LocationCard = (props) => {
  const {
    mb,
    expanded = false,
    location,
    variant = "card",
    showSectionTitle = false,
    mapHeight,
  } = props;

  if (variant === "panel") {
    return (
      <LocationPanel
        location={location}
        mb={mb}
        showSectionTitle={showSectionTitle}
        mapHeight={mapHeight}
      />
    );
  }

  return (
    <LocationAccordionCard location={location} mb={mb} expanded={expanded} />
  );
};

LocationCard.propTypes = {
  location: AddressPropTypes.isRequired,
  mb: PropTypes.any,
  expanded: PropTypes.bool,
  variant: PropTypes.oneOf(["panel", "card"]),
  showSectionTitle: PropTypes.bool,
  mapHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
